/**
 * NotificationBell — campanita del panel admin
 * Muestra el total de pendientes (todos los módulos), con popover detallado
 * y toggle de sonido. Se auto-actualiza cada 60s.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BellOff, MessageCircle, FileText, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import useAdminNotifications from '../hooks/useAdminNotifications';

const ICONO_MODULO = {
  gestoria: FileText,
};

export const NotificationBell = ({ className = '' }) => {
  const { total, modulos, soundEnabled, toggleSound, refresh } = useAdminNotifications();
  const [open, setOpen] = useState(false);

  const tieneModulos = Object.keys(modulos || {}).length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`relative inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-slate-100 transition-colors ${className}`}
          aria-label="Notificaciones"
          data-testid="admin-notifications-bell"
        >
          {total > 0 ? <Bell className="h-5 w-5 text-slate-700" /> : <BellOff className="h-5 w-5 text-slate-400" />}
          {total > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-md ring-2 ring-white">
              {total > 99 ? '99+' : total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b flex items-center justify-between">
          <p className="font-semibold text-slate-900">Notificaciones</p>
          <Button
            size="sm"
            variant="ghost"
            onClick={toggleSound}
            className="text-xs h-7 px-2"
            title={soundEnabled ? 'Silenciar sonido' : 'Activar sonido'}
            data-testid="admin-notifications-sound-toggle"
          >
            {soundEnabled ? (
              <><Volume2 className="h-3.5 w-3.5 mr-1" /> Sonido</>
            ) : (
              <><VolumeX className="h-3.5 w-3.5 mr-1 text-slate-400" /> Muteado</>
            )}
          </Button>
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {!tieneModulos || total === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              <Bell className="h-6 w-6 mx-auto mb-2 text-slate-300" />
              Todo al día. No hay novedades.
            </div>
          ) : (
            <div className="divide-y">
              {Object.entries(modulos).map(([key, mod]) => {
                if (!mod || mod.total === 0) return null;
                const Icon = ICONO_MODULO[key] || Bell;
                return (
                  <Link
                    key={key}
                    to={mod.url || '/admin'}
                    onClick={() => { setOpen(false); refresh(); }}
                    className="flex items-start gap-3 p-3 hover:bg-violet-50 transition-colors"
                    data-testid={`admin-notifications-item-${key}`}
                  >
                    <div className="bg-violet-100 text-violet-700 p-2 rounded-lg flex-shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900">{mod.label}</p>
                      <div className="text-xs text-slate-600 space-y-0.5 mt-0.5">
                        {mod.solicitudes_no_leidas > 0 && (
                          <p className="flex items-center gap-1"><FileText className="h-3 w-3" /> {mod.solicitudes_no_leidas} solicitud{mod.solicitudes_no_leidas === 1 ? '' : 'es'} sin leer</p>
                        )}
                        {mod.mensajes_no_leidos > 0 && (
                          <p className="flex items-center gap-1"><MessageCircle className="h-3 w-3" /> {mod.mensajes_no_leidos} mensaje{mod.mensajes_no_leidos === 1 ? '' : 's'} del cliente</p>
                        )}
                      </div>
                    </div>
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full h-6 min-w-[24px] px-1.5 flex items-center justify-center">
                      {mod.total}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-2 border-t bg-slate-50 text-center">
          <p className="text-[10px] text-slate-400">Actualiza cada 60 segundos</p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
